import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/* =========================================
   STORAGE
========================================= */

const STORAGE_KEY = 'freeGiftState';
window.enableGiftthreshold = true;

window.getGiftState = function () {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
  } catch {
    return {};
  }
}

window.setGiftState = function (next) {
  const prev = getGiftState();
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prev, ...next }));
}

window.resetGiftState = function () {
  localStorage.removeItem(STORAGE_KEY);
}

/* =========================================
   CART FETCH
========================================= */

window.getCartFresh = async function () {
  try {
    const res = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!res.ok) throw new Error('Cart fetch failed');
    return await res.json();
  } catch (e) {
    console.error('[FreeGift] Cart fetch error:', e);
    return null;
  }
}

/* =========================================
   HELPERS
========================================= */
window.closePopup = function () {
  document.querySelector('#free-gift-dialog dialog')?.close();
}

document.addEventListener('DOMContentLoaded', () => {

  const dialog = document.querySelector('#free-gift-dialog');

  if (!dialog) return;

  dialog.addEventListener('close', () => {

    const state = getGiftState();

    if (!state.paidProductAdded) {
      setGiftState({ firstPopupClosed: true });
    } else {
      setGiftState({ secondPopupClosed: true });
    }

  });

});

function findGift(cart) {
  return cart?.items?.find(i => i.properties?._free_gift === 'Yes');
}

function countPaid(cart) {
  return (cart?.items || []).filter(
    i => i.properties?._free_gift !== 'Yes'
  ).length;
}

/* =========================================
   CHECKOUT LOCK
========================================= */

async function toggleCheckoutLock(cart) {

  const gift = findGift(cart);
  const paidCount = countPaid(cart);

  const checkoutBtn =
    document.querySelector('[name="checkout"]') ||
    document.querySelector('.cart__checkout-button');

  if (!checkoutBtn) return;

  if (gift && paidCount === 0) {

    checkoutBtn.disabled = true;
    checkoutBtn.classList.add('checkout-disabled');
    checkoutBtn.title = "Add a product before checkout";

  } else {
    checkoutBtn.disabled = false;
    checkoutBtn.classList.remove('checkout-disabled');
    checkoutBtn.removeAttribute('title');

  }

}

window.isKlaviyoOpen = function () {
  return !!document.querySelector('[data-testid="POPUP"]');
}

window.waitForKlaviyoToClose = function (callback) {
  const interval = setInterval(() => {
    if (!isKlaviyoOpen()) {
      clearInterval(interval);
      callback();
    }
  }, 300);
}

window.openPopup = function () {
  const dialog = document.querySelector('#free-gift-dialog dialog');

  if (!dialog) {
    console.warn('[FreeGift] Popup not found in DOM');
    return false;
  }

  // 🚫 If Klaviyo is open → wait
  if (isKlaviyoOpen()) {
    waitForKlaviyoToClose(() => {
      dialog.showModal();
    });
    return false;
  }

  dialog.showModal();
  return true;
}

/* =========================================
   TIMED POPUP (BEFORE ADD TO CART)
========================================= */

document.addEventListener('DOMContentLoaded', async () => {

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('drawstring') === 'open') {
    openPopup();
    return;
  }

  const enablePrePopup = window.FREE_GIFT_BEFORE_CART ?? true;
  const delay = window.FREE_GIFT_DELAY ?? 10; // seconds

  //if (!enablePrePopup) return;

  const state = getGiftState();
  // Do not show again if already shown in session
  // if (state.popupShown) return;
  //if (state.firstPopupClosed) return;

  const cart = await getCartFresh();
  // if (!cart) return;

  // Only show timed popup if cart is empty
  // if (cart.item_count !== 0) return;
  setTimeout(() => {
    const stateNow = getGiftState();
    if (stateNow.popupShown) return;
    const show = () => {
      if (openPopup()) {
        setGiftState({ popupShown: true });
      }
    };
    if (isKlaviyoOpen()) {
      waitForKlaviyoToClose(show);
    } else {
      show();
    }
  }, delay * 1000);

});

document.addEventListener('click', function (e) {
  const btn = e.target.closest('.change-gift');
  if (!btn) return;

  e.preventDefault();
  document.querySelector('#free-gift-dialog dialog')?.showModal();
});

/* =========================================
   CORE RULE ENGINE
========================================= */

let settleTimer = null;

document.addEventListener(ThemeEvents.cartUpdate, () => {
  clearTimeout(settleTimer);
  
  settleTimer = setTimeout(async () => {
    console.log("LOAD");
    const cart = await getCartFresh();
    if (!cart?.items) return;

    await toggleCheckoutLock(cart);

    const gift = findGift(cart);
    const paidCount = countPaid(cart);
    const state = getGiftState();

    const freeGiftConfig = window.FREE_GIFT_CONFIG || {
      total: 0,
      available: 0
    };

    /* =====================
       CART EMPTY
    ===================== */
    if (cart.item_count === 0) {
      resetGiftState();
      return;
    }

    /* =====================
       NO AVAILABLE FREE GIFTS
       → DO NOTHING
    ===================== */
    // if (freeGiftConfig.available === 0) {
    //   return;
    // }

    /* =====================
       MANUAL GIFT REMOVAL
    ===================== */
    if (paidCount > 0 && state.added && !gift) {
      setGiftState({ removedManually: true });
    }

    /* =====================
       AUTO ADD SINGLE GIFT
    ===================== */
    async function autoAddSingleGift() {
      const radio = document.querySelector('.gift-radio');
      if (!radio?.value) return;

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: radio.value,
          quantity: 1,
          properties: { _free_gift: 'Yes' }
        })
      });

      setGiftState({
        added: true,
        removedManually: false,
        popupShown: true
      });

      if (openPopup()) {
        setGiftState({ popupShown: true });
      }

      document.dispatchEvent(
        new CartUpdateEvent({ source: 'free-gift-auto-added' })
      );
    }

    /* =====================
       PAID ITEMS EXIST
    ===================== */
    if (!state.paidProductAdded && paidCount > 0) {
      setGiftState({
        paidProductAdded: true
      });

      // If gift already exists → never show popup again
      if (gift) {
        setGiftState({ added: true });
        return;
      }

      if (!state.secondPopupClosed) {
        openPopup();
      }

      // ================================
      // 2. Respect manual removal
      // ================================
      if (state.removedManually) return;

      // ================================
      // 3. FORCE MANUAL MODE
      // ================================
      if (window.ALLOW_FREE_GIFT === false) {
        if (!state.popupShown) {
          openPopup();
          setGiftState({ popupShown: true });
        }
        return;
      }

      // ================================
      // 4. Prevent popup spam
      // ================================
      // Allow second popup when paid product added
      if (state.firstPopupClosed && state.paidProductAdded) {
        // allow popup
      } else if (state.firstPopupClosed) {
        return;
      }

      // Prefer Liquid total, fallback to DOM
      const totalGiftProducts =
        typeof window.FREE_GIFT_TOTAL === 'number'
          ? window.FREE_GIFT_TOTAL
          : document.querySelectorAll('.gift-card .gift-radio').length;

      const variantSelects = document.querySelectorAll('.gift-variant-select');

      // ================================
      // SINGLE PRODUCT
      // ================================
      if (totalGiftProducts === 1) {

        if (variantSelects.length === 1) {
          const select = variantSelects[0];
          const enabledOptions = [...select.options].filter(o => !o.disabled);

          if (enabledOptions.length === 1) {
            await autoAddSingleGift();
            return;
          }

          if (openPopup()) {
            setGiftState({ popupShown: true });
          }
          return;
        }

        await autoAddSingleGift();
        return;
      }

      // ================================
      // MULTIPLE PRODUCTS
      // ================================
      if (totalGiftProducts > 1) {
        if (openPopup()) {
          setGiftState({ popupShown: true });
        }
        return;
      }
    }

    /* =====================
       REMOVE GIFT WHEN NO PAID ITEMS
    ===================== */
    if (paidCount === 0 && gift) {
      const removeGift = window.FREE_GIFT_REMOVE_IF_NO_PAID ?? true;

      if (removeGift && cart.item_count === 1) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: gift.key,
            quantity: 0
          })
        });

        resetGiftState();

        document.dispatchEvent(
          new CartUpdateEvent({ source: 'free-gift-auto-remove' })
        );

      } else {
        // Just disable checkout instead
        await toggleCheckoutLock(cart);

      }

    }
  }, 4000);
});

/* =========================================
   ADD GIFT BUTTON (MANUAL)
========================================= */

document.addEventListener('DOMContentLoaded', () => {
  const addBtn = document.querySelector(
    '#free-gift-dialog .add-to-cart-button'
  );
  if (!addBtn) return;

  addBtn.addEventListener('click', async () => {
    const selected = document.querySelector('.gift-radio:checked');
    if (!selected?.value) {
      alert('Please select a free gift');
      return;
    }

    try {
      addBtn.disabled = true;
      addBtn.classList.add('atc-added');

      // 🔥 Get fresh cart
      const cart = await getCartFresh();
      if (!cart?.items) return;

      const existingGift = findGift(cart);

      /* =========================
         IF GIFT ALREADY EXISTS
         → REMOVE FIRST
      ========================== */
      if (existingGift) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingGift.key,
            quantity: 0
          })
        });
      }

      /* =========================
         ADD NEW SELECTED GIFT
      ========================== */
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selected.value,
          quantity: 1,
          properties: { _free_gift: 'Yes' }
        })
      });

      if (!res.ok) throw new Error('Gift add failed');

      setGiftState({
        added: true,
        removedManually: false,
        popupShown: true
      });

      closePopup();

      document.dispatchEvent(
        new CartUpdateEvent({ source: 'free-gift-replaced' })
      );

    } catch (err) {
      console.error('[FreeGift] Add error:', err);
    } finally {
      addBtn.classList.remove('atc-added');
      addBtn.disabled = false;
    }
  });
});

/* =========================================
   VARIANT RADIO SYNC
========================================= */

document.addEventListener('change', (e) => {
  if (!e.target.classList.contains('gift-variant-select')) return;

  const select = e.target;
  const selectedOption = select.options[select.selectedIndex];
  const card = select.closest('.gift-card');
  const radio = card?.querySelector('.gift-radio');

  if (!radio) return;

  if (selectedOption.disabled) {
    radio.disabled = true;
    radio.checked = false;
  } else {
    radio.disabled = false;
    radio.checked = true;
    radio.value = selectedOption.value;
  }
});

/* =========================================
   MANUAL OPEN BUTTON
========================================= */

document.addEventListener('click', (e) => {
  if (!e.target.matches('#open-free-gift-popup, .ayf-gift')) return;

  setGiftState({
    removedManually: false,
    popupShown: true,
    paidProductAdded: false,
    firstPopupClosed: false,
    secondPopupClosed: false,
  });

  if (isKlaviyoOpen()) {
    waitForKlaviyoToClose(() => openPopup());
  } else {
    openPopup();
  }
});


document.addEventListener('change', async (e) => {
  if (!e.target.matches('.cart-variant-selector')) return;

  const select = e.target;
  const container = select.closest('.cart-items__variants');
  const variantsJSON = container.querySelector('.product-variants');

  if (!variantsJSON) {
    console.error('Variants JSON missing');
    return;
  }

  const variants = JSON.parse(variantsJSON.textContent);
  const selects = container.querySelectorAll('.cart-variant-selector');

  const selectedOptions = [];

  selects.forEach(s => {
    selectedOptions.push(s.options[s.selectedIndex].text.trim());
  });

  const matchedVariant = variants.find(v =>
    v.options.every((opt, i) => opt === selectedOptions[i])
  );

  if (!matchedVariant) {
    console.log('No matching variant');
    return;
  }

  const oldKey = select.dataset.key;

  try {

    /* remove old item */
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: oldKey,
        quantity: 0
      })
    });

    /* small delay prevents race condition */
    await new Promise(resolve => setTimeout(resolve, 200));

    /* add new variant */
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: matchedVariant.id,
          quantity: 1
        }]
      })
    });

    document.dispatchEvent(
        new CartUpdateEvent({ source: 'free-gift-auto-added' })
      );

  } catch (err) {
    console.error('Variant swap failed', err);
  }
});

document.addEventListener('click', function () {
  const klaviyoClose = document.querySelector('.klaviyo-close-form');

  if (klaviyoClose) {
    klaviyoClose.addEventListener('click', () => {
      setTimeout(() => {
        openPopup();
      }, 800);
    });
  }
});



  // document.addEventListener("DOMContentLoaded", function () {
  //   const videos = document.querySelectorAll('.js-autoplay-video');

  //   videos.forEach(video => {
  //     video.play().catch(() => {
  //       // Autoplay blocked (battery saver / iOS)
  //       const playOnTouch = () => {
  //         video.play();
  //         video.removeEventListener('touchstart', playOnTouch);
  //         video.removeEventListener('click', playOnTouch);
  //       };

  //       video.addEventListener('touchstart', playOnTouch);
  //       video.addEventListener('click', playOnTouch);
  //     });
  //   });
  // });

document.addEventListener("DOMContentLoaded", function () {
  const videos = document.querySelectorAll('.js-autoplay-video');
  let hasPlayed = false; // ✅ guard flag

  const playAllVideos = () => {
    if (hasPlayed) return; // 🚫 prevent multiple calls
    hasPlayed = true;

    videos.forEach(video => {
      video.play().catch(() => {});
    });
  };

  // First user interaction anywhere
  document.addEventListener('touchstart', playAllVideos, { once: true });
  document.addEventListener('click', playAllVideos, { once: true });
});


// Slideshow change events
document.addEventListener('DOMContentLoaded', () => {
  const slideshow = document.querySelector('slideshow-component');

  if (!slideshow) return;

  const targetContainer = document.querySelector('#slide_wear_size');

  // Allowed sizes
  const validSizes = ["XS", "S", "M", "L", "XL", "XXL"];

  slideshow.addEventListener('slideshow:select', (event) => {
    const { index, slide } = event.detail;

    document.body.setAttribute('data-active-slide', index);

    if (!slide || !targetContainer) return;

    const mediaEl = slide.querySelector('.product-media[data-image-attribute]');

    if (mediaEl) {
      let value = mediaEl.getAttribute('data-image-attribute');

      if (value) {
        value = value.trim().toUpperCase(); // normalize

        if (validSizes.includes(value)) {
          targetContainer.innerHTML = `<div class="centered waring-size size_indicator font-12">${value}</div>`;
          targetContainer.classList.add('active');
          return;
        }
      }
    }

    // If not valid → clear
    targetContainer.innerHTML = '';
    targetContainer.classList.remove('active');
  });

  // Gift popup
  const body = document.body;
  const earlyDialog = document.querySelector('#early-access-success dialog');
  const dialogPass = document.querySelector('#early-access-password dialog');
  const passwordInput = document.querySelector('#access-password');
  const submitBtn = document.querySelector('#submit-password');
  const errorMsg = document.querySelector('#password-error');
  const closeBtn = document.querySelector('#close-password');

  const params = new URLSearchParams(window.location.search);
  const type = params.get('type');

  const CORRECT_PASSWORD = "EARLY2026";
  // if (type === 'early-access') {
  //   earlyDialog.showModal();
  // }

  // Open popup if query matches
  if (type === 'early-access-password' && dialogPass) {
    // dialogPass.showModal();
    // body.classList.add('early-access-active');
  }

  // Submit password
  // if (submitBtn) {
  //   submitBtn.addEventListener('click', () => {
  //     const entered = passwordInput.value.trim();

  //     if (entered === CORRECT_PASSWORD) {
  //       dialogPass.close();
  //       body.classList.remove('early-access-active');
  //       errorMsg.style.display = 'none';

  //       // ✅ Remove query param from URL
  //       const url = new URL(window.location);
  //       url.searchParams.delete('type');
  //       window.history.replaceState({}, document.title, url.pathname + url.search);
        
  //     } else {
  //       errorMsg.style.display = 'block';
  //     }
  //   });
  // }

  // Close button (optional)
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      dialogPass.close();
      body.classList.remove('early-access-active');
    });
  }

  // Also handle native dialog close (ESC or backdrop click)
  if (dialogPass) {
    dialogPass.addEventListener('close', () => {
      body.classList.remove('early-access-active');
    });
  }

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.mother-gift-btn');
    if (!btn) return;

    e.preventDefault();

    const card = btn.closest('.gift-card');
    const radio = card?.querySelector('.gift-radio');
    const select = card?.querySelector('.gift-variant-select');

    let variantId = radio?.value;

    if (select) {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption && !selectedOption.disabled) {
        variantId = selectedOption.value;
      }
    }

    if (!variantId) {
      alert('Please select a variant');
      return;
    }

    try {
      btn.disabled = true;
      btn.classList.add('atc-added');

      const cart = await getCartFresh();
      if (!cart?.items) return;

      const existingGift = findGift(cart);

      if (existingGift) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: existingGift.key,
            quantity: 0
          })
        });
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _free_gift: 'Yes' }
        })
      });

      if (!res.ok) throw new Error('Gift add failed');

      setGiftState({
        added: true,
        removedManually: false,
        popupShown: true
      });

      document.dispatchEvent(
        new CartUpdateEvent({ source: 'free-gift-added-from-card' })
      );

    } catch (err) {
      console.error('[FreeGift] Add error:', err);
    } finally {
      btn.classList.remove('atc-added');
      btn.disabled = false;
    }
  });
});



// Header Swatch
document.addEventListener("DOMContentLoaded", function () {
  const slider = document.querySelector(".color-slider");
  const nextBtn = document.querySelector(".color-nav.next");
  const prevBtn = document.querySelector(".color-nav.prev");
  const sliderWraper = document.querySelector(".color-slider-wrapper");

  if (!slider || !nextBtn || !prevBtn) return;

  const scrollAmount = 72;

  function updateButtons() {
    const maxScrollLeft = slider.scrollWidth - slider.clientWidth;

    // LEFT
    if (slider.scrollLeft <= 2) {
      prevBtn.classList.add("hidden");
      sliderWraper.classList.add("hide-prev");
    } else {
      prevBtn.classList.remove("hidden");
      sliderWraper.classList.remove("hide-prev");
    }

    // RIGHT
    if (slider.scrollLeft >= maxScrollLeft - 2) {
      nextBtn.classList.add("hidden");
      sliderWraper.classList.add("hide-next");
    } else {
      nextBtn.classList.remove("hidden");
     sliderWraper.classList.remove("hide-next");
    }
  }

  function checkOverflow() {
    const hasOverflow = slider.scrollWidth > slider.clientWidth + 2;

    if (hasOverflow) {
      nextBtn.classList.remove("hidden");
      slider.classList.remove("hide-next");
    } else {
      nextBtn.classList.add("hidden");
      prevBtn.classList.add("hidden");
      slider.classList.remove("hide-prev");
      slider.classList.remove("hide-next");
    }
  }

  function init() {
    checkOverflow();
    updateButtons();
  }

  nextBtn.addEventListener("click", () => {
    slider.scrollBy({ left: scrollAmount, behavior: "smooth" });
  });

  prevBtn.addEventListener("click", () => {
    slider.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  });

  slider.addEventListener("scroll", updateButtons);

  // ✅ ONLY ONE reliable init trigger
  window.addEventListener("load", () => {
    init();
  });

  // ✅ Resize fix
  window.addEventListener("resize", init);

  // ✅ BEST FIX (handles image load + layout changes perfectly)
  // const observer = new ResizeObserver(() => {
  //   init();
  // });

  // observer.observe(slider);

}); 


// New Product Template Tabs
function initProductTabs() {
  console.log("loadd");
  document.querySelectorAll(".product-tabs-component").forEach((component) => {
    console.log("tabsss", component);

    const nav = component.querySelector("[data-tabs-nav]");
    const content = component.querySelector("[data-tabs-content]");

    if (!nav || !content) return;

    const items = content.querySelectorAll(".product-tab-item");

    items.forEach((item, index) => {

      const button = item.querySelector(".product-tab-pill");
      const panel = item.querySelector(".product-tab-panel");

      if (!button || !panel) return;

      // Move pills into top nav
      nav.appendChild(button);

      // First active
      if (index === 0) {

        button.classList.add("active");
        button.setAttribute("aria-selected", "true");
        button.setAttribute("tabindex", "0");

        panel.classList.add("active");
        panel.hidden = false;

      } else {

        button.setAttribute("aria-selected", "false");
        button.setAttribute("tabindex", "-1");

        panel.hidden = true;

      }

      button.addEventListener("click", () => {

        const targetId = button.dataset.tabTrigger;

        // Reset buttons
        nav.querySelectorAll(".product-tab-pill").forEach((pill) => {

          pill.classList.remove("active");
          pill.setAttribute("aria-selected", "false");
          pill.setAttribute("tabindex", "-1");

        });

        // Reset panels
        content.querySelectorAll(".product-tab-panel").forEach((tabPanel) => {

          tabPanel.classList.remove("active");
          tabPanel.hidden = true;

        });

        // Active button
        button.classList.add("active");
        button.setAttribute("aria-selected", "true");
        button.setAttribute("tabindex", "0");

        // Active panel
        const activePanel = content.querySelector(`#${targetId}`);

        if (activePanel) {

          activePanel.classList.add("active");
          activePanel.hidden = false;

        }

        // Smooth scroll active pill
        button.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest"
        });

      });

      // Keyboard support
      button.addEventListener("keydown", (event) => {

        const tabs = [...nav.querySelectorAll(".product-tab-pill")];

        const currentIndex = tabs.indexOf(button);

        let nextIndex = currentIndex;

        switch (event.key) {

          case "ArrowRight":
            nextIndex = (currentIndex + 1) % tabs.length;
            break;

          case "ArrowLeft":
            nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
            break;

          case "Home":
            nextIndex = 0;
            break;

          case "End":
            nextIndex = tabs.length - 1;
            break;

          default:
            return;

        }

        event.preventDefault();

        tabs[nextIndex].focus();
        tabs[nextIndex].click();

      });

    });

    // Mouse wheel horizontal scroll
    nav.addEventListener("wheel", (event) => {

      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {

        event.preventDefault();

        nav.scrollLeft += event.deltaY;

      }

    }, { passive: false });

    component.classList.add("tabs-ready");

  });
}

document.addEventListener("DOMContentLoaded", initProductTabs);


// Delivery Dates
(function() {

    let isUpdating = false;

    async function updateDeliveryDate() {

        if (isUpdating) return;

        const elements = document.querySelectorAll('.custom-delivery-date');

        if (!elements.length) return;

        isUpdating = true;

        try {

            /*
            ============================================
            SHIPPING MATRIX
            ============================================
            */

            const SHIPPING_RULES = {
                DKK: { min: 1, max: 2 },
                EUR: { min: 1, max: 2 },
                GBP: { min: 1, max: 3 },
                CHF: { min: 1, max: 3 },
                SEK: { min: 1, max: 2 },
                NOK: { min: 1, max: 2 },
                PLN: { min: 1, max: 2 },

                AED: { min: 1, max: 3 },
                SAR: { min: 1, max: 3 },

                CAD: { min: 1, max: 3 },

                MXN: { min: 2, max: 4 },

                AUD: { min: 2, max: 4 },
                HKD: { min: 2, max: 4 },
                SGD: { min: 2, max: 4 },
                JPY: { min: 2, max: 4 },
                KRW: { min: 2, max: 4 },
                CNY: { min: 2, max: 4 }
            };

            /*
            ============================================
            GET SHOPIFY MARKET CURRENCY
            ============================================
            */

            let currency = 'USD';

            if (window.Shopify?.currency?.active) {
                currency = Shopify.currency.active;
            } else if (window.Shopify?.checkout?.currency) {
                currency = Shopify.checkout.currency;
            }

            /*
            ============================================
            GEO IP LOOKUP
            ============================================
            */

            async function getUserCountry() {

                try {

                    const cachedCountry = localStorage.getItem('user_country');
                    const cachedTime = localStorage.getItem('user_country_time');

                    if (
                        cachedCountry &&
                        cachedTime &&
                        (Date.now() - parseInt(cachedTime, 10) < 86400000)
                    ) {
                        return cachedCountry;
                    }

                    const response = await Promise.race([
                        fetch('https://ipapi.co/json/').then(r => r.json()),
                        new Promise((_, reject) =>
                            setTimeout(() => reject(new Error('timeout')), 3000)
                        )
                    ]);

                    const country =
                        response.country_code ||
                        response.country ||
                        'US';

                    localStorage.setItem('user_country', country);
                    localStorage.setItem('user_country_time', Date.now().toString());

                    return country;

                } catch (e) {

                    return 'US';
                }
            }

            const userCountry = await getUserCountry();

            /*
            ============================================
            SHIPPING RANGE
            ============================================
            */

            function getShippingRange(currency, country) {

                /*
                USD SPECIAL LOGIC
                */

                if (currency === 'USD') {

                    return country === 'US'
                        ? { min: 1, max: 3 }
                        : { min: 2, max: 4 };
                }

                /*
                STANDARD RULES
                */

                if (SHIPPING_RULES[currency]) {
                    return SHIPPING_RULES[currency];
                }

                /*
                FALLBACK
                */

                return { min: 2, max: 4 };
            }

            const shippingRange = getShippingRange(currency, userCountry);

            /*
            ============================================
            HOLIDAYS
            ONLY FOR SHIPPING DATE
            ============================================
            */

            async function getHolidays(countryCode) {

                if (!countryCode) return [];

                const year = new Date().getFullYear();

                const cacheKey = `holidays_${countryCode}_${year}`;

                const cached = localStorage.getItem(cacheKey);

                if (cached) {
                    return JSON.parse(cached);
                }

                try {

                    const response = await fetch(
                        `https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`
                    );

                    if (!response.ok) return [];

                    const data = await response.json();

                    const dates = data.map(h => h.date);

                    localStorage.setItem(cacheKey, JSON.stringify(dates));

                    return dates;

                } catch (e) {

                    return [];
                }
            }

            /*
            SHIPPING COUNTRY
            Denmark warehouse
            */

            const holidays = await getHolidays('DK');

            /*
            ============================================
            HELPERS
            ============================================
            */

            function isWeekend(date) {

                const day = date.getDay();

                return day === 0 || day === 6;
            }

            function isHoliday(date, holidays) {

                const formatted = date.toISOString().split('T')[0];

                return holidays.includes(formatted);
            }

            /*
            ============================================
            CET CUTOFF
            ============================================
            */

            let baseDate = new Date();

            const cetHour = parseInt(
                new Intl.DateTimeFormat('en-US', {
                    timeZone: 'Europe/Copenhagen',
                    hour: 'numeric',
                    hour12: false
                }).format(new Date()),
                10
            );

            /*
            AFTER 14:00 CET
            START NEXT DAY
            */

            if (cetHour >= 12) {

                baseDate.setDate(baseDate.getDate() + 1);
            }

            /*
            ============================================
            SHIPPING DATE
            HOLIDAYS ONLY APPLY HERE
            ============================================
            */

            let shippingDate = new Date(baseDate.getTime());

            while (
                isWeekend(shippingDate) ||
                isHoliday(shippingDate, holidays)
            ) {
                shippingDate.setDate(shippingDate.getDate() + 1);
            }

            /*
            ============================================
            ADD BUSINESS DAYS
            ONLY SKIP WEEKENDS
            HOLIDAYS DO NOT AFFECT TRANSIT
            ============================================
            */

            function addBusinessDays(date, days) {

                const result = new Date(date.getTime());

                let added = 0;

                while (added < days) {

                    result.setDate(result.getDate() + 1);

                    /*
                    ONLY SKIP WEEKENDS
                    */

                    if (!isWeekend(result)) {

                        added++;
                    }
                }

                return result;
            }

            /*
            ============================================
            DELIVERY RANGE
            ============================================
            */

            const minDate = addBusinessDays(
                shippingDate,
                shippingRange.min
            );

            const maxDate = addBusinessDays(
                shippingDate,
                shippingRange.max
            );

            /*
            ============================================
            FORMAT
            ============================================
            */

            function formatDeliveryRange(minDate, maxDate, isCartDrawer = false) {

                const minMonth = minDate.toLocaleDateString(undefined, {
                    month: 'long'
                });

                const maxMonth = maxDate.toLocaleDateString(undefined, {
                    month: 'long'
                });

                const minDay = minDate.getDate();
                const maxDay = maxDate.getDate();

                /*
                BOLD MONTH + DATES
                */

                const formattedDate = (minMonth === maxMonth)
                    ? `<strong>${minMonth} ${minDay} – ${maxDay}</strong>`
                    : `<strong>${minMonth} ${minDay} – ${maxMonth} ${maxDay}</strong>`;

                /*
                DIFFERENT TEXT FOR CART
                */

                const prefix = isCartDrawer
                    ? 'Free delivery on your order - arrives '
                    : 'Order now - arrives ';

                return `${prefix}${formattedDate}`;
            }

           /*
            ============================================
            UPDATE PRODUCT PAGE
            ============================================
            */

            document.querySelectorAll('.product-delivery-date')
                .forEach(el => {
                    const text = formatDeliveryRange(
                        minDate,
                        maxDate,
                        false
                    );

                    if (el.innerHTML !== text) {

                        el.innerHTML = text;
                        el.style.opacity = '1';
                    }
                });

            /*
            ============================================
            UPDATE CART DRAWER
            ============================================
            */

            document.querySelectorAll('.cart-delivery-date')
                .forEach(el => {

                    const text = formatDeliveryRange(
                        minDate,
                        maxDate,
                        true
                    );

                    if (el.innerHTML !== text) {

                        el.innerHTML = text;
                        el.style.opacity = '1';
                    }
                });

                    } catch (error) {

                        console.error('Delivery Date Error:', error);

                    } finally {

                        isUpdating = false;
                    }
                }

    /*
    ============================================
    EVENTS
    ============================================
    */

    document.addEventListener(
        'DOMContentLoaded',
        updateDeliveryDate
    );

    document.addEventListener('cart:updated', () => {
        console.log("Updated");
        setTimeout(updateDeliveryDate, 200);
    });

    document.addEventListener('cart:added', () => {

        setTimeout(updateDeliveryDate, 500);
    });

    document.addEventListener('click', function(e) {

        if (
            e.target.closest(
                'a[href="/cart"], .cart-toggle, #cart-icon-bubble'
            )
        ) {
            setTimeout(updateDeliveryDate, 400);
        }
    });

    /*
    ============================================
    OBSERVER
    ============================================
    */

    const observer = new MutationObserver(() => {

        if (
            !isUpdating &&
            document.querySelector('.custom-delivery-date')
        ) {
            updateDeliveryDate();
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

})();